import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import { getTrackInfo, linkedTracks } from 'api/axios/crux/bcon'

const initialState = {
  visible: false,
  isSubmitting: false,
  previewVisible: false,
  isLoading: false,
  loadingError: undefined,
  isLoadingTracks: false,
  tracksLoadingErrorVisible: false,
  hasTalkChannel: false,
  campaigns: [],
  tracks: [],
  deletedTracks: {},
  form: {
    conversionTrackingId: '',
  },
  preview: {
    conversionTrackingId: '',
  },
}

const fetchLinkedTracks = createAxiosThunk(
  'dashboard/fetchLinkedTracks',
  async (trackIds, { getState }) => {
    const adAccountId = getState().account.current.id
    const {
      data: {
        body: { content: tracks },
      },
    } = await linkedTracks({
      page: 0,
      adAccountId,
      size: 100,
    })
    const deletedTrackIds = trackIds.filter(id =>
      tracks.every(({ trackId }) => trackId !== id)
    )
    const results = await Promise.all(
      deletedTrackIds.map(deletedId =>
        getTrackInfo({ trackId: deletedId, adAccountId })
          .then(
            ({
              data: {
                body: { trackId, name },
              },
            }) => ({ trackId, name })
          )
          .catch(() => null)
      )
    )
    const deletedTracks = results
      .filter(Boolean)
      .reduce((acc, { trackId, name }) => {
        acc[trackId] = name
        return acc
      }, {})
    return api.response({ tracks, deletedTracks })
  }
)

export const showCampaignConversionTrackingEdit = createAxiosThunk(
  'dashboard/showCampaignConversionTrackingEdit',
  async ({ campaignIds }, { dispatch }) => {
    const { data } = await api.ad.getCampaignsById(campaignIds)
    const campaigns = data.campaigns.map(
      ({ id, name, conversionTrackingId }) => ({
        id,
        name,
        conversionTrackingId,
      })
    )
    const trackIds = [
      ...new Set(
        campaigns
          .map(({ conversionTrackingId }) => conversionTrackingId)
          .filter(Boolean)
      ),
    ]
    dispatch(fetchLinkedTracks(trackIds))
    return api.response(campaigns)
  }
)

export const updateCampaignsConversionTracking = createAxiosThunk(
  'dashboard/updateCampaignsConversionTracking',
  (noArgs, { getState }) => {
    const {
      campaigns,
      form: { conversionTrackingId },
    } = getState().dashboard.operation.campaignConversionTrackingEdit
    return api.ad.patchCampaignsConversionTracking({
      campaignIds: campaigns.map(({ id }) => id),
      conversionTrackingId:
        conversionTrackingId === '' ? null : conversionTrackingId,
    })
  }
)
const campaignConversionTrackingEditSlice = createSlice({
  name: 'dashboard/campaignConversionTrackingEdit',
  initialState,

  reducers: {
    hideCampaignConversionTrackingEdit: () => initialState,
    setConversionTrackingId: (state, { payload }) => {
      state.form.conversionTrackingId = payload
    },
    showPreview: state => {
      state.preview.conversionTrackingId = state.form.conversionTrackingId
      state.previewVisible = true
    },
    hidePreview: state => {
      state.previewVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(
      showCampaignConversionTrackingEdit.pending,
      (state, { meta }) => {
        state.isLoading = true
        state.visible = true
        state.hasTalkChannel = meta.arg.hasTalkChannel
      }
    )

    builder.addCase(
      showCampaignConversionTrackingEdit.rejected,
      (state, { payload }) => {
        state.loadingError = payload
        state.isLoading = false
      }
    )

    builder.addCase(
      showCampaignConversionTrackingEdit.fulfilled,
      (state, { payload }) => {
        state.campaigns = payload
        const hasSameValue =
          payload.length > 1 &&
          payload.every(
            (value, index, array) =>
              value.conversionTrackingId === array[0].conversionTrackingId
          )
        if (payload.length === 1 || hasSameValue) {
          const [first] = payload
          const { conversionTrackingId } = first
          state.form.conversionTrackingId = conversionTrackingId
        }
        state.visible = true
        state.isLoading = false
      }
    )

    builder.addCase(fetchLinkedTracks.pending, state => {
      state.isLoadingTracks = true
    })

    builder.addCase(fetchLinkedTracks.rejected, state => {
      state.isLoadingTracks = false
      state.tracksLoadingErrorVisible = true
    })

    builder.addCase(
      fetchLinkedTracks.fulfilled,
      (state, { payload: { tracks, deletedTracks } }) => {
        state.tracks = tracks
        state.deletedTracks = deletedTracks
        state.isLoadingTracks = false
      }
    )

    builder.addCase(updateCampaignsConversionTracking.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(updateCampaignsConversionTracking.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(updateCampaignsConversionTracking.rejected, state => {
      state.isSubmitting = false
    })
  },
})
export const {
  hideCampaignConversionTrackingEdit,
  showPreview,
  hidePreview,
  setConversionTrackingId,
} = campaignConversionTrackingEditSlice.actions
export default campaignConversionTrackingEditSlice.reducer
